import { useContext, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { useEditor, useNode } from "@craftjs/core";
import MuiButton from "@mui/material/Button";
import ContentEditable from "react-contenteditable";
import useResponsiveValues from "@/components/editor/hooks/useResponsiveValues";
import ButtonBlockSettings from "@/components/editor/settings/ButtonSettings/ButtonBlockSettings";
import useNormalizeButtonSettings from "@/components/editor/settings/ButtonSettings/useNormalizeButtonSettings";
import { CssClass } from "@/components/editor/settings/ClassSettings";
import { useNavigate } from "react-router-dom";
import { ButtonProps, ButtonTarget } from "../settings/ButtonSettings/ButtonSettings";
import useVariableValues from "../hooks/useVariableValues";
import { RepeatContext } from "./RepeatBlock";

type Props = CssClass & ButtonProps;

const ButtonBlock = (props: Partial<Props>) => {
  const {
    id,
    connectors: { connect, drag },
    actions: { setProp },
    hasSelectedNode,
  } = useNode((state) => {
    return {
      hasSelectedNode: state.events.selected,
      hasDraggedNode: state.events.dragged,
    };
  });

  const { query } = useEditor();
  const responsive = useResponsiveValues();
  const [editable, setEditable] = useState(false);
  const buttonProps = useNormalizeButtonSettings(props);
  const vv = useVariableValues();
  const isEditorEnabled = query.getOptions().enabled;
  const navigate = useNavigate();
  const repeatContext = useContext(RepeatContext);
  const formik = useFormikContext<any>();

  const handleClick = () => {
    if (isEditorEnabled) return;
    if (repeatContext.href) {
      window.open(repeatContext.href);
      return;
    }
    if (props?.url && typeof props?.url === "string") {
      const url = vv.getValue(props?.url ?? "");

      if (responsive.getValue(props?.target) === ButtonTarget.BLANK) {
        window.open(url, "_blank");
      } else {
        if (url.startsWith("?")) {
          const currentParams = new URLSearchParams(window.location.search);
          const incomingParams = new URLSearchParams(url.substring(1));

          for (const [key, value] of incomingParams) {
            currentParams.append(key, value);
          }

          const scrollToId = currentParams.get("scrollTo");
          const hasDrawerNone = currentParams.getAll("drawer").includes("none");

          const cleanParams = new URLSearchParams(currentParams);
          if (hasDrawerNone) {
            cleanParams.delete("drawer");
          }
          cleanParams.delete("scrollTo");

          const cleanUrl = `${window.location.pathname}?${cleanParams.toString()}`;

          navigate(cleanUrl, {
            replace: true,
            preventScrollReset: true,
          });

          if (scrollToId) {
            setTimeout(() => {
              const element = document.getElementById(scrollToId);
              if (element) {
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    element.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                      inline: "nearest",
                    });
                  });
                });
              }
            }, 500);
          }
        } else {
          navigate(url);
        }
      }
    }
  };

  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    !hasSelectedNode && setEditable(false);
  }, [hasSelectedNode]);

  const type = (responsive.getValue(props.type) as any) || "button";
  const isSubmitting = formik?.isSubmitting;
  const isDisabled = (!isEditorEnabled || !editable) && !isEditorEnabled && type === "submit" && isSubmitting;

  return (
    <MuiButton key={`${id}-${responsive.viewport}`} ref={(ref: any) => connect(drag(ref))} type={type} {...(buttonProps as any)} disabled={isDisabled} onClick={handleClick}>
      <ContentEditable
        className={`text-block ${responsive.getValue(props?.cssClass)}`}
        html={repeatContext?.title || buttonProps?.text}
        onChange={(e) => {
          setProp((props: Props) => (props.text = e.target.value.replace(/<\/?[^>]+(>|$)/g, "")));
        }}
        disabled={!isEditorEnabled || !editable}
        onClick={(_) => setEditable(true)}
      />
    </MuiButton>
  );
};

ButtonBlock.craft = {
  displayName: "Botón",
  props: {
    text: "Reemplaza",
    href: "Test",
    type: "button",
  },
  related: {
    settings: ButtonBlockSettings,
  },
};

export default ButtonBlock;
